import { Course } from "@/types/course";
import { DocumentInfo, DocumentView } from "@/types/document";
import { useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { CourseDialog } from "../courses/CourseDialog";
import { DocumentImport } from "./DocumentImport";
import { DocumentItem } from "./DocumentItem";
import { DocumentsHeaders } from "./DocumentsHeaders";
import { EmptyState } from "./EmptyState";

interface DocumentsProps {
  currentView: DocumentView;
  filteredDocuments: DocumentInfo[];
  loadingDocId: string | null;
  selectedCourseIds: string[];
  searchQuery: string;
  coursesExcludingOnboarding: Course[];
  onCourseFilterChange: (ids: string[]) => void;
  onSearchChange: (query: string) => void;
  onOpenDocument: (doc: DocumentInfo) => void;
}

export const Documents = ({
  currentView,
  filteredDocuments,
  loadingDocId,
  selectedCourseIds,
  searchQuery,
  coursesExcludingOnboarding,
  onCourseFilterChange,
  onSearchChange,
  onOpenDocument,
}: DocumentsProps) => {
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isCreateCourseDialogOpen, setIsCreateCourseDialogOpen] =
    useState(false);

  // Reset focused index when view changes, course filter changes, or search query changes
  useEffect(() => {
    setFocusedIndex(0);
  }, [currentView, selectedCourseIds, searchQuery]);

  useHotkeys(
    "up",
    () => {
      setFocusedIndex((prev) => Math.max(0, prev - 1));
    },
    { preventDefault: true }
  );

  useHotkeys(
    "down",
    () => {
      setFocusedIndex((prev) =>
        Math.min(filteredDocuments.length - 1, prev + 1)
      );
    },
    { preventDefault: true }
  );

  useHotkeys(
    "enter",
    () => {
      const doc = filteredDocuments[focusedIndex];
      if (doc) {
        onOpenDocument(doc);
      }
    },
    { preventDefault: true }
  );

  return (
    <div className="flex flex-col xl:max-w-[1000px] max-w-[800px] px-20 py-20 mx-auto w-full">
      <DocumentsHeaders
        currentView={currentView}
        filteredDocumentsCount={filteredDocuments.length}
        selectedCourseIds={selectedCourseIds}
        searchQuery={searchQuery}
        onCourseFilterChange={onCourseFilterChange}
        onSearchChange={onSearchChange}
        onSearchFocus={() => setIsSearching(true)}
        onSearchBlur={() => setIsSearching(false)}
      />

      {filteredDocuments.length === 0 &&
        selectedCourseIds.length === 0 &&
        !searchQuery.trim() && (
          <EmptyState
            view={currentView}
            alert={coursesExcludingOnboarding.length === 0}
            onAddCourse={() => setIsCreateCourseDialogOpen(true)}
          />
        )}

      <div className="h-[calc(100vh-380px)] overflow-y-auto">
        <div className="flex flex-col gap-4 pr-4">
          {filteredDocuments.map((doc, index) => (
            <DocumentItem
              key={doc.id}
              doc={doc}
              loadingDocId={loadingDocId}
              onclick={onOpenDocument}
              isFocused={index === focusedIndex}
              tabIndex={!isSearching ? index : null}
            />
          ))}
        </div>
      </div>

      {currentView === "ready" && (
        <div className="w-full mt-8 sticky bottom-0 bg-white flex items-center justify-center">
          <DocumentImport />
        </div>
      )}

      <CourseDialog
        isOpen={isCreateCourseDialogOpen}
        onClose={() => setIsCreateCourseDialogOpen(false)}
      />
    </div>
  );
};
